import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import axios from 'axios';
import { config } from '../config';

interface RegistrationForm {
  email: string;
  password: string;
  confirmPassword: string;
  userType: 'admin' | 'teacher' | 'parent' | 'student';
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
}

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().min(8, 'Password must be at least 8 characters').required('Password is required'),
  confirmPassword: yup.string().oneOf([yup.ref('password')], 'Passwords must match').required('Confirm password is required'),
  userType: yup.string().oneOf(['admin', 'teacher', 'parent', 'student']).required('User type is required'),
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  phone: yup.string().required('Phone number is required'),
  address: yup.string().required('Address is required')
});

const UserRegistration: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm<RegistrationForm>({
    resolver: yupResolver(schema)
  });

  const onSubmit = async (data: RegistrationForm) => {
    setLoading(true);
    try {
      const response = await axios.post(`${config.BACKEND_URL}/api/users/register`, {
        email: data.email,
        password: data.password,
        userType: data.userType,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        address: data.address
      });

      if (response.data.success) {
        toast.success('User registered successfully!');
        reset();
      } else {
        toast.error(response.data.error || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <h2>Register New User</h2>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              {...register('firstName')}
              type="text"
              id="firstName"
              placeholder="Enter first name"
            />
            {errors.firstName && <span className="error">{errors.firstName.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              {...register('lastName')}
              type="text"
              id="lastName"
              placeholder="Enter last name"
            />
            {errors.lastName && <span className="error">{errors.lastName.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              {...register('email')}
              type="email"
              id="email"
              placeholder="Enter email address"
            />
            {errors.email && <span className="error">{errors.email.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              {...register('phone')}
              type="tel"
              id="phone"
              placeholder="Enter phone number"
            />
            {errors.phone && <span className="error">{errors.phone.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="address">Address</label>
            <input
              {...register('address')}
              type="text"
              id="address"
              placeholder="Enter address"
            />
            {errors.address && <span className="error">{errors.address.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="userType">User Type</label>
            <select {...register('userType')} id="userType">
              <option value="">Select user type</option>
              <option value="admin">Administrator</option>
              <option value="teacher">Teacher</option>
              <option value="parent">Parent</option>
              <option value="student">Student</option>
            </select>
            {errors.userType && <span className="error">{errors.userType.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              {...register('password')}
              type="password"
              id="password"
              placeholder="Enter password"
            />
            {errors.password && <span className="error">{errors.password.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              {...register('confirmPassword')}
              type="password"
              id="confirmPassword"
              placeholder="Confirm password"
            />
            {errors.confirmPassword && <span className="error">{errors.confirmPassword.message}</span>}
          </div>

          <button type="submit" className="btn" disabled={loading}>
            {loading ? 'Registering...' : 'Register User'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default UserRegistration;
